import { throttle } from 'lodash-es';
import { Dispatch, SetStateAction } from 'react';

import { makePopoutId, popoutWindowDivId } from './generateId';
import {
  addInsertRuleListener,
  copyStyleSheets,
  observeStyleSheetChanges,
  removeInsertRuleListener,
} from './handleStyleSheetsChanges';
import { PanelPosition, PanelSizeExact } from './types';

export type OpenExternalWindowResponse = {
  externalWindow: Window | null;
  onCloseExternalWindow: (() => void) | null;
};
/**
 * Open and scaffold the new external window.
 *
 * The main motivation for extracting these procedures into a function is for testing ease. JSDOM does not mock out the
 * entire window object, making the PopoutWindow component difficult to unit test without extensive mocking. Instead, we
 * can avoid mocking most of the window methods entirely by conditionally calling an alternative to `openExternalWindow`
 * during unit testing by providing a truthy `openExternalWindowFn` prop.
 */
export const openExternalWindow = ({
  title,
  size,
  setContainerElement,
  setHeadElement,
  observeStyleChanges = false,
  className,
  position,
}: {
  title: string;
  size: PanelSizeExact;
  setContainerElement: Dispatch<SetStateAction<HTMLElement | undefined>>;
  setHeadElement: Dispatch<SetStateAction<HTMLElement | undefined>>;
  observeStyleChanges?: boolean;
  className?: string;
  position: PanelPosition;
}): OpenExternalWindowResponse => {
  const response: OpenExternalWindowResponse = { externalWindow: null, onCloseExternalWindow: null };

  // NOTE:
  // 1/ Edge ignores the left and top settings
  // 2/ Chrome has a bug that screws up the window size by a factor dependent on your windows scaling settings
  // if you open the browser in a window with a scaling and drag it to a window with a different scaling
  const features =
    `scrollbars=no,resizable=no,status=no,location=no,toolbar=no,menubar=no,` +
    `width=${size.width},height=${size.height},left=${position.x},top=${position.y}`;
  response.externalWindow = window.open('', makePopoutId(title), features);

  if (response.externalWindow) {
    const { externalWindow } = response;

    // Reset the default body style applied by the browser
    externalWindow.document.body.style.margin = '0';
    externalWindow.document.body.style.padding = '0';

    // Remove any existing body HTML from this window
    const existingBodyMountElement = externalWindow.document.body?.firstChild;
    if (existingBodyMountElement) {
      externalWindow.document.body.removeChild(existingBodyMountElement);
    }
    // Create a new HTML element to hang our rendering off
    const newMountElement = externalWindow.document.createElement('div');
    newMountElement.className = `PopoutWindowContainer ${className || ''}`;
    setContainerElement(newMountElement);
    externalWindow.document.body.appendChild(newMountElement);

    // Do the same for the head node (where all the styles are added)
    const existingHeadMountElement = externalWindow.document.head?.firstChild;
    if (existingHeadMountElement) {
      externalWindow.document.head.removeChild(existingHeadMountElement);
    }
    // and an HTML element to hang our copied styles off
    const newHeadMountElement = externalWindow.document.createElement('div');
    externalWindow.document.head.appendChild(newHeadMountElement);
    setHeadElement(newHeadMountElement);

    // Set External window title
    externalWindow.document.title = title;

    // Set an id to the external window div to be queried and used by react-modal as parentSelector
    // so the modal shows up in the popped out window instead the main window
    newMountElement.id = popoutWindowDivId(title);

    copyStyleSheets(newHeadMountElement);

    // Always intercept insertRule so styles injected after the initial copy (e.g. react-select
    // rendering after the window opens) are synced to the popout. Uses a shared throttled
    // callback so both this and the optional MutationObserver drive the same copy call.
    const throttledCopy = throttle(() => copyStyleSheets(newHeadMountElement), 100);
    addInsertRuleListener(throttledCopy);

    let observer: MutationObserver | null = null;
    if (observeStyleChanges) {
      observer = observeStyleSheetChanges(throttledCopy);
    }

    externalWindow.onbeforeunload = () => {
      observer?.disconnect();
      removeInsertRuleListener(throttledCopy);
    };

    response.onCloseExternalWindow = () => {
      // Make sure the window closes when the component unmounts
      externalWindow.close();
    };
  }

  return response;
};
